import React from 'react';
import { useBranding } from '../contexts/BrandingContext';

/**
 * DashboardSkeleton - A reusable skeleton loader that matches the application's dashboard layout.
 * Used during authentication transitions and data loading states.
 *
 * @dependencies
 * - BrandingContext: Dynamic platform name and logo
 */
const DashboardSkeleton = () => {
  const { branding } = useBranding();
  const platformName = branding.identity.platformName;
  const logoMarkUrl = branding.assets.logoMarkUrl;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header Skeleton - matches AppHeader structure */}
      <header className="bg-gray-900 text-white h-16 fixed top-0 right-0 left-0 z-30 flex items-center">
        <div className="flex items-center w-full">
          <div className="flex-1 flex items-center">
            {/* Logo area - show actual logo, no pulse bar here */}
            <div className="hidden lg:flex shrink-0 items-center justify-center" style={{ width: '200px' }}>
              {logoMarkUrl ? (
                <img src={logoMarkUrl} alt={platformName} className="h-8 w-auto" />
              ) : (
                <span className="text-brand font-bold text-xl">{platformName}</span>
              )}
            </div>

            {/* Mobile logo area */}
            <div className="lg:hidden flex items-center px-4">
              {logoMarkUrl ? (
                <img src={logoMarkUrl} alt={platformName} className="h-8 w-auto" />
              ) : (
                <span className="text-brand font-bold text-xl">{platformName}</span>
              )}
            </div>

            <div className="flex-1 flex items-center gap-4 min-w-0 px-4">
              {/* Search bar placeholder */}
              <div className="flex-1">
                <div className="w-full lg:max-w-md lg:mx-auto">
                  <div className="w-full h-10 bg-gray-800 rounded-full animate-pulse" />
                </div>
              </div>
            </div>
          </div>

          {/* Right side placeholder (Profile/Notifications) */}
          <div className="flex items-center space-x-4 px-4">
            <div className="w-5 h-5 bg-gray-800 rounded animate-pulse" />
            <div className="w-8 h-8 bg-gray-700 rounded-full animate-pulse" />
          </div>
        </div>
      </header>

      {/* Sidebar Skeleton - starts below header like real sidebar */}
      <div className="hidden lg:flex w-[200px] bg-gray-900 fixed left-0 top-16 h-[calc(100vh-4rem)] flex-col">
        <nav className="mt-4 px-3 space-y-1">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="w-full h-10 bg-gray-800 rounded animate-pulse" />
          ))}
        </nav>
      </div>

      {/* Main Content Area */}
      <main className="lg:ml-[200px] mt-16 p-4 md:p-6 lg:p-8">
        {/* Page Title/Header area skeleton */}
        <div className="mb-8">
          <div className="w-48 h-8 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="w-64 h-4 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Main Card Skeleton */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="space-y-4">
            <div className="w-full h-4 bg-gray-200 rounded animate-pulse" />
            <div className="w-full h-4 bg-gray-200 rounded animate-pulse" />
            <div className="w-3/4 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>

        {/* Dashboard Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="w-20 h-4 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="w-32 h-8 bg-gray-200 rounded animate-pulse" />
            </div>
          ))}
        </div>

        {/* Bottom sections skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="w-32 h-5 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="h-48 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="w-40 h-5 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
                  <div className="flex-1">
                    <div className="w-3/4 h-4 bg-gray-200 rounded animate-pulse mb-1" />
                    <div className="w-1/2 h-3 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="w-16 h-6 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardSkeleton;
